import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

/* ----------------------------- DTOs & Props ----------------------------- */
interface LocationDTO { id: number; name: string; }
interface BuildingDTO { id: number; name: string; locationId?: number; }
interface FloorDTO { id: number; name: string; buildingId?: number; }
interface RoomDTO { id: number; name: string; floorId?: number; buildingId?: number; }

interface UserDTO {
  id: number;
  empNumber: string;
  name: string;
  departmentNames: string[];
  userType: string;
  email: string;
  phone: string;
}

interface MaintenanceTaskDTO {
  id: number;
  locationId: number | null;
  buildingId: number | null;
  floorId: number | null;
  roomId: number | null;
  description: string;
  taskScheduleId: number | null;
  startDateTime: string;
  endDateTime: string | null;
  scheduleType?: 'DAILY' | 'WEEKLY' | 'MONTHLY' | string | null;
  customDays?: string | null;
  occurrenceCount?: number | null;
  isRecurring: boolean;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  permanentResponsibleAllocate: boolean;
  responsiblePersons: UserDTO[];
  accountablePersons: UserDTO[];
}

interface Props {
  show: boolean;
  onHide: () => void;
  task: MaintenanceTaskDTO;
  locations: LocationDTO[];
  buildings: BuildingDTO[];
  floors: FloorDTO[];
  rooms: RoomDTO[];
}

/* ----------------------------- Helpers ----------------------------- */
const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString() : 'N/A';

/* ----------------------------- Component ----------------------------- */
const ViewMaintenanceTaskModule: React.FC<Props> = ({
  show, onHide, task, locations, buildings, floors, rooms
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [fullTask, setFullTask] = useState<MaintenanceTaskDTO | null>(null);
  const [locationName, setLocationName] = useState<string>('N/A');
  const [buildingName, setBuildingName] = useState<string>('N/A');
  const [floorName, setFloorName] = useState<string>('N/A');
  const [roomName, setRoomName] = useState<string>('N/A');
  const [responsiblePersons, setResponsiblePersons] = useState<UserDTO[]>([]);
  const [accountablePersons, setAccountablePersons] = useState<UserDTO[]>([]);

  const recurringFlag = fullTask?.isRecurring ?? false;

  useEffect(() => {
    if (!show || !task?.id) return;

    const run = async () => {
      if (!checkPermission('ROLE_GET_TASK')) {
        showError('You do not have permission to view maintenance task details.');
        onHide();
        return;
      }

      setIsLoading(true);
      setFullTask(null);
      setResponsiblePersons([]);
      setAccountablePersons([]);

      try {
        const res = await apiDefinitions.getMaintenanceTaskById(task.id);
        if (res?.data?.status === 'S1000') {
          const apiTask: MaintenanceTaskDTO = res.data.data;
          const normalized: MaintenanceTaskDTO = {
            ...apiTask,
            isRecurring: apiTask.isRecurring ?? false,
          };
          setFullTask(normalized);
          await resolveAllNames(normalized);

          setResponsiblePersons(normalized.responsiblePersons);
          setAccountablePersons(normalized.accountablePersons);
        } else {
          showError(res?.data?.statusDescription || 'Failed to fetch maintenance task');
          onHide();
        }
      } catch (e: any) {
        showError(e?.response?.data?.statusDescription || 'Error fetching maintenance task');
        onHide();
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [show, task?.id]);

  const resolveAllNames = async (t: MaintenanceTaskDTO) => {
    // Handle Location, Building, Floor, Room resolution here.
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>View Maintenance Task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading || !fullTask ? (
          <div className="text-center my-3">
            <Spinner animation="border" role="status" />
            <span className="visually-hidden">Loading...</span>
          </div>
        ) : (
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control type="text" value={fullTask.description || 'N/A'} readOnly />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control type="text" value={locationName} readOnly />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Building</Form.Label>
                  <Form.Control type="text" value={buildingName} readOnly />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Floor</Form.Label>
                  <Form.Control type="text" value={floorName} readOnly />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Room</Form.Label>
                  <Form.Control type="text" value={roomName} readOnly />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Recurring</Form.Label>
                  <Form.Control type="text" value={recurringFlag ? 'Yes' : 'No'} readOnly />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Control type="text" value={fullTask.status || 'N/A'} readOnly />
                </Form.Group>
              </Col>

              <Col md={6}>

              
            <Row className="mt-3">
              {/* Responsible Persons */}
              <Col md={12}>
                <Form.Label>Responsible Persons</Form.Label>
                {responsiblePersons.length > 0 ? (
                  responsiblePersons.map((person, index) => (
                    <Form.Group key={index} className="mb-2">
                      <Form.Control
                        type="text"
                        value={`${person.empNumber} - ${person.name}`}
                        readOnly
                      />
                    </Form.Group>
                  ))
                ) : (
                  <Form.Control type="text" value="N/A" readOnly />
                )}
              </Col>

              {/* Accountable Persons */}
              <Col md={12}>
                <Form.Label>Accountable Persons</Form.Label>
                {accountablePersons.length > 0 ? (
                  accountablePersons.map((person, index) => (
                    <Form.Group key={index} className="mb-2">
                      <Form.Control
                        type="text"
                        value={`${person.empNumber} - ${person.name}`}
                        readOnly
                      />
                    </Form.Group>
                  ))
                ) : (
                  <Form.Control type="text" value="N/A" readOnly />
                )}
              </Col>
            </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date & Time</Form.Label>
                  <Form.Control type="text" value={formatDateTime(fullTask.startDateTime)} readOnly />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>End Date & Time</Form.Label>
                  <Form.Control type="text" value={formatDateTime(fullTask.endDateTime)} readOnly />
                </Form.Group>


                <Form.Group className="mb-3">
                  <Form.Label>Permanent Responsible Allocation</Form.Label>
                  <Form.Control
                    type="text"
                    value={fullTask.permanentResponsibleAllocate ? 'Yes' : 'No'}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>


            <Row className="mt-1">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Schedule Type</Form.Label>
                  <Form.Control type="text" value={fullTask.scheduleType ?? 'N/A'} readOnly />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Custom Days</Form.Label>
                  <Form.Control type="text" value={fullTask.customDays ?? 'N/A'} readOnly />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Occurrence Count</Form.Label>
                  <Form.Control type="text" value={(fullTask.occurrenceCount ?? 'N/A') as any} readOnly />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-2">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Created By</Form.Label>
                  <Form.Control type="text" value={fullTask.createdBy || 'N/A'} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Created At</Form.Label>
                  <Form.Control type="text" value={formatDateTime(fullTask.createdAt)} readOnly />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewMaintenanceTaskModule;
